// /settings/sms — SMS Gateway (Twilio) — G1.4
//
// Per-tenant BYO Twilio credentials (AES-256-GCM at rest). OWNER writes.
// SUPER_ADMIN / ADMIN / MODERATOR get a read-only view for support.
// MANAGER / CASHIER are blocked at the route level (see router.tsx).
// Traceability: FR-NOT-*, NFR-039 (secrets at rest), DLT / TRAI compliance.

import { useState, useEffect, useCallback } from 'react';
import {
  PageContainer, Button, Input, Skeleton, ErrorBoundary, useToast,
} from '@bbpos/ui';
import { smsService } from '@/services/sms.service';
import { ApiError } from '@/lib/api-client';
import { useAuthStore } from '@/stores/use-auth-store';
import { GLASS, GLASS_FORM, cn } from '@/constants/design-tokens';
import type { SmsConfigMasked } from '@bbpos/types';

const ACCOUNT_SID_REGEX = /^AC[a-f0-9]{32}$/i;
const MESSAGING_SID_REGEX = /^MG[a-f0-9]{32}$/i;
const E164_REGEX = /^\+[1-9]\d{6,14}$/;

export function SmsConfigPage() {
  const { toast } = useToast();
  const role = useAuthStore((s) => s.user?.role ?? s.profile?.role ?? '');
  const canEdit = role === 'OWNER';
  const isPlatform = role === 'SUPER_ADMIN' || role === 'ADMIN' || role === 'MODERATOR';

  const [existing, setExisting] = useState<SmsConfigMasked | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [accountSid, setAccountSid] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [fromNumber, setFromNumber] = useState('');
  const [messagingServiceSid, setMessagingServiceSid] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await smsService.getConfig();
      setExisting(cfg);
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 404) {
        setExisting(null);
      } else if (err instanceof ApiError) {
        toast({ variant: 'error', title: 'Failed to load SMS config', description: err.message });
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { document.title = 'SMS Gateway — Orio360 Admin'; }, []);

  const handleSave = async () => {
    if (!ACCOUNT_SID_REGEX.test(accountSid.trim())) {
      toast({ variant: 'warning', title: 'Enter a valid Account SID (AC… 34 chars)' });
      return;
    }
    if (authToken.trim().length < 16) {
      toast({ variant: 'warning', title: 'Auth Token looks too short' });
      return;
    }
    const from = fromNumber.trim();
    const msid = messagingServiceSid.trim();
    if (!from && !msid) {
      toast({ variant: 'warning', title: 'Enter a From number OR a Messaging Service SID' });
      return;
    }
    if (from && !E164_REGEX.test(from)) {
      toast({ variant: 'warning', title: 'From number must be E.164 (e.g. +15551234567)' });
      return;
    }
    if (msid && !MESSAGING_SID_REGEX.test(msid)) {
      toast({ variant: 'warning', title: 'Messaging Service SID must start with MG (34 chars)' });
      return;
    }
    setSaving(true);
    try {
      const saved = await smsService.upsertConfig({
        account_sid: accountSid.trim(),
        auth_token: authToken.trim(),
        from_number: from || undefined,
        messaging_service_sid: msid || undefined,
        is_active: true,
      });
      setExisting(saved);
      setAccountSid('');
      setAuthToken('');
      setFromNumber('');
      setMessagingServiceSid('');
      toast({
        variant: 'success',
        title: 'Twilio credentials saved',
        description: `Auth token ending in ${saved.auth_token_last4}`,
      });
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Could not save credentials',
        description: err instanceof ApiError ? err.message : 'Save failed',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await smsService.testConfig();
      setTestResult({ ok: res.ok, error: res.error });
      if (res.ok) toast({ variant: 'success', title: 'Twilio responded OK' });
      else toast({ variant: 'warning', title: 'Test failed', description: res.error });
      await load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Test failed';
      setTestResult({ ok: false, error: msg });
      toast({ variant: 'error', title: 'Test failed', description: msg });
    } finally {
      setTesting(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm('Deactivate Twilio SMS? Outbound SMS will stop immediately.')) return;
    try {
      await smsService.deactivateConfig();
      toast({ variant: 'success', title: 'SMS credentials deactivated' });
      setExisting(null);
      await load();
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Deactivation failed',
        description: err instanceof ApiError ? err.message : 'Could not deactivate',
      });
    }
  };

  const copySender = async () => {
    const value = existing?.messaging_service_sid || existing?.from_number;
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast({ variant: 'success', title: 'Copied to clipboard' });
    } catch {
      toast({ variant: 'warning', title: 'Clipboard unavailable', description: value });
    }
  };

  return (
    <ErrorBoundary>
      <PageContainer>
        {/* Hero — no flat white, gradient, subtle glow */}
        <div className="relative overflow-hidden bg-gradient-to-r from-brand-600 via-indigo-600 to-teal-600 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(14,116,144,0.55)] mb-6 animate-fade-in">
          <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-white/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-white/5 blur-3xl" />
          <div className="relative flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-brand-100 mb-1">Integrations · Notifications</div>
              <h1 className="text-2xl font-bold text-white">SMS Gateway · Twilio</h1>
              <p className="text-brand-100 mt-1 text-sm max-w-2xl">
                Per-tenant Twilio account. Auth tokens are AES-256-GCM encrypted at rest;
                GET responses return only a <span className="font-mono">••••last4</span> preview.
                Transactional SMS in India requires TRAI DLT registration on a sender ID owned by your business.
              </p>
            </div>
            <SmsGlyph />
          </div>
        </div>

        {/* Platform read-only banner */}
        {isPlatform && (
          <div className={cn(GLASS, 'p-4 mb-6 flex items-start gap-3 border-l-4 border-brand-500')}>
            <div className="w-8 h-8 rounded-lg bg-brand-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">i</div>
            <div className="text-sm text-neutral-700 dark:text-neutral-300">
              <div className="font-semibold text-neutral-900 dark:text-white">Platform read-only</div>
              You're viewing this tenant's SMS configuration for support purposes.
              Only the tenant <span className="font-semibold">OWNER</span> can save, rotate, or deactivate credentials.
            </div>
          </div>
        )}

        {/* Three status cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <StatusCard existing={existing} loading={loading} />
          <SenderCard existing={existing} onCopy={copySender} />
          <TestCard existing={existing} testing={testing} onTest={handleTest} result={testResult} canEdit={canEdit} />
        </div>

        {/* Credentials form */}
        <div className={cn(GLASS_FORM, 'p-6 animate-slide-up')}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-neutral-500 dark:text-neutral-400">
              {existing ? 'Rotate credentials' : 'Configure credentials'}
            </h2>
            {!canEdit && (
              <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
                OWNER only
              </span>
            )}
          </div>
          {loading ? (
            <Skeleton className="h-48" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Account SID"
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                value={accountSid}
                onChange={(v: string) => setAccountSid(v)}
                helperText="Twilio Console → Account Info → Account SID"
                disabled={!canEdit}
                className="font-mono"
              />
              <div className="flex flex-col">
                <Input
                  label="Auth Token"
                  type={showToken ? 'text' : 'password'}
                  placeholder="••••••••••••••••••••••••••••••••"
                  value={authToken}
                  onChange={(v: string) => setAuthToken(v)}
                  helperText="Twilio Console → Account Info → Auth Token. Rotate regularly."
                  disabled={!canEdit}
                  className="font-mono"
                />
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => setShowToken((v) => !v)}
                    className="text-xs text-brand-600 dark:text-brand-400 self-end mt-1 hover:underline"
                  >
                    {showToken ? 'Hide' : 'Show'} token while typing
                  </button>
                )}
              </div>
              <Input
                label="From number"
                placeholder="+15551234567"
                value={fromNumber}
                onChange={(v: string) => setFromNumber(v)}
                helperText="E.164 format. Required if no Messaging Service SID."
                disabled={!canEdit}
                className="font-mono tabular-nums"
              />
              <Input
                label="Messaging Service SID (optional)"
                placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                value={messagingServiceSid}
                onChange={(v: string) => setMessagingServiceSid(v)}
                helperText="Preferred. Twilio routes to DLT-registered sender in the service."
                disabled={!canEdit}
                className="font-mono"
              />
              <div className="md:col-span-2 flex flex-wrap gap-3 items-center justify-end pt-2">
                {existing && canEdit && (
                  <Button variant="secondary" onClick={handleDeactivate}>
                    Deactivate
                  </Button>
                )}
                <Button
                  onClick={handleSave}
                  loading={saving}
                  disabled={!canEdit || !accountSid || !authToken || (!fromNumber && !messagingServiceSid)}
                >
                  {existing ? 'Rotate & save' : 'Save credentials'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* DLT compliance notice */}
        <div className={cn(GLASS, 'p-5 mt-6 border-l-4 border-warning-500')}>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-warning-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">DLT</div>
            <div className="text-sm text-neutral-700 dark:text-neutral-300">
              <div className="font-semibold text-neutral-900 dark:text-white mb-1">TRAI DLT registration is mandatory</div>
              India requires every transactional or promotional SMS template to be pre-registered
              on a DLT platform (Jio, Airtel, Vi, BSNL). Your Twilio sender ID (from-number or
              Messaging Service) must be tied to your registered header &amp; templates — unregistered
              traffic gets blocked at the operator level. We do not auto-register templates.
            </div>
          </div>
        </div>

        {/* Security notice — mirrors PaymentGatewayPage tone */}
        <div className={cn(GLASS, 'p-5 mt-4 border-l-4 border-success-500')}>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-success-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">SEC</div>
            <div className="text-sm text-neutral-700 dark:text-neutral-300">
              <div className="font-semibold text-neutral-900 dark:text-white mb-1">Encrypted at rest</div>
              Auth tokens are encrypted with AES-256-GCM using a key never exposed to the
              application process. Reads are Redis-cached for 5 minutes and invalidated on
              every rotation. Audit logs record who rotated, when — never the plaintext.
            </div>
          </div>
        </div>
      </PageContainer>
    </ErrorBoundary>
  );
}

// ─── Cards ────────────────────────────────────────────────────────────────

function StatusCard({ existing, loading }: { existing: SmsConfigMasked | null; loading: boolean }) {
  const color = existing?.is_active
    ? 'from-success-500 to-teal-500'
    : existing
      ? 'from-warning-500 to-amber-500'
      : 'from-neutral-400 to-neutral-500';
  const pillTone = existing?.is_active
    ? 'bg-success-50 text-success-700 border-success-200 dark:bg-success-900/30 dark:text-success-300'
    : existing
      ? 'bg-warning-50 text-warning-700 border-warning-200 dark:bg-warning-900/30 dark:text-warning-300'
      : 'bg-neutral-100 text-neutral-700 border-neutral-200 dark:bg-neutral-800 dark:text-neutral-300';
  return (
    <div className={cn(GLASS, 'p-5')}>
      <div className="flex items-center gap-3 mb-3">
        <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center text-white bg-gradient-to-br text-xs font-bold tracking-wider', color)} aria-hidden>SID</div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Status</span>
      </div>
      {loading ? (
        <Skeleton className="h-8 w-32" />
      ) : existing ? (
        <div className="space-y-2">
          <div className="text-lg font-bold tabular-nums font-mono text-neutral-900 dark:text-white break-all">
            {existing.account_sid_masked}
          </div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">
            Auth token {existing.auth_token_masked}
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className={cn('inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider rounded-full border', pillTone)}>
              {existing.is_active ? 'Active' : 'Inactive'}
            </span>
            <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
              Updated {new Date(existing.updated_at).toLocaleString('en-IN')}
            </span>
          </div>
        </div>
      ) : (
        <div>
          <div className="text-lg font-semibold text-neutral-900 dark:text-white">Not configured</div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            Outbound SMS falls back to dev-mock in non-production. Production sends will be rejected as terminal.
          </div>
        </div>
      )}
    </div>
  );
}

function SenderCard({ existing, onCopy }: { existing: SmsConfigMasked | null; onCopy: () => void }) {
  const usingService = Boolean(existing?.messaging_service_sid);
  const sender = existing?.messaging_service_sid || existing?.from_number || null;
  return (
    <div className={cn(GLASS, 'p-5')}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold tracking-wider" aria-hidden>TX</div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Sender identity</span>
      </div>
      {sender ? (
        <>
          <div className="flex items-center gap-2 mb-2">
            <span className={cn(
              'inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider rounded-full border',
              usingService
                ? 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300'
                : 'bg-brand-50 text-brand-700 border-brand-200 dark:bg-brand-900/30 dark:text-brand-300',
            )}>
              {usingService ? 'Messaging Service' : 'From number'}
            </span>
          </div>
          <div className="font-mono text-sm text-neutral-800 dark:text-neutral-200 break-all mb-3 p-2 rounded-md bg-neutral-50 dark:bg-neutral-800/60 tabular-nums">
            {sender}
          </div>
          <Button size="sm" variant="secondary" onClick={onCopy} className="w-full">Copy sender ID</Button>
        </>
      ) : (
        <div className="text-xs text-neutral-500 dark:text-neutral-400">
          Sender will appear here after you save a From number or Messaging Service SID.
        </div>
      )}
    </div>
  );
}

function TestCard({
  existing, testing, onTest, result, canEdit,
}: {
  existing: SmsConfigMasked | null;
  testing: boolean;
  onTest: () => void;
  result: { ok: boolean; error?: string } | null;
  canEdit: boolean;
}) {
  const last = existing?.last_tested_at
    ? `${existing.last_test_status ?? '—'} · ${new Date(existing.last_tested_at).toLocaleString('en-IN')}`
    : 'Never';
  return (
    <div className={cn(GLASS, 'p-5')}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-brand-500 to-indigo-500 flex items-center justify-center text-white text-xs font-bold tracking-wider" aria-hidden>OK?</div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Liveness</span>
      </div>
      <div className="text-xs text-neutral-600 dark:text-neutral-300 mb-2">
        Last tested <span className="tabular-nums font-mono">{last}</span>
      </div>
      {existing?.last_test_error && !result && (
        <div className="text-[11px] mb-3 p-2 rounded-md bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300 font-mono break-all">
          {existing.last_test_error}
        </div>
      )}
      {result && (
        <div className={cn(
          'text-xs mb-3 p-2 rounded-md',
          result.ok
            ? 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-300'
            : 'bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300',
        )}>
          {result.ok ? 'Twilio responded OK' : (result.error || 'Test failed')}
        </div>
      )}
      <Button
        onClick={onTest}
        loading={testing}
        disabled={!existing || !canEdit}
        className="w-full"
        variant={existing ? 'primary' : 'secondary'}
      >
        Test connection
      </Button>
    </div>
  );
}

function SmsGlyph() {
  return (
    <svg className="hidden md:block h-14 w-14 text-white/80" fill="none" viewBox="0 0 48 48" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 10h32a2 2 0 012 2v20a2 2 0 01-2 2H20l-8 8V34H8a2 2 0 01-2-2V12a2 2 0 012-2z" />
      <path d="M14 18h20M14 24h14" />
    </svg>
  );
}
